import { Children, cloneElement, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services/message-service/index.js';
import { isDefined, isTouchableDevice } from '@philips-internal/oswald-ui-utils';
import { TabStyle } from '../tab-constants.js';
import { ICON_WIDTH_AND_MARGIN } from '../tab-control/tab-control-constants.js';
function getPreTabElementWidth(tab) {
    const { badge, count, icon, label, style } = tab.props;
    const labelElement = document.createElement('div');
    const labelContent = style !== TabStyle.ICON_ONLY ? `<span class="c-internal-tab__text">${MessageService.translate(label)}</span>` : '';
    const hasIconInline = icon && (style === TabStyle.ICON_AND_TEXT_INLINE || style === TabStyle.ICON_ONLY);
    const badgeElement = badge || count ? `<span class="c-internal-tab__badge">(${count || badge})</span>` : '';
    const iconWidthAndMargin = hasIconInline ? ICON_WIDTH_AND_MARGIN : 0;
    labelElement.className = 'c-pre-tab c-internal-tab';
    labelElement.innerHTML = `${labelContent}${badgeElement}`;
    document.body.appendChild(labelElement);
    const labelElementRect = labelElement.getBoundingClientRect();
    const elementWidth = labelElementRect.width + iconWidthAndMargin;
    document.body.removeChild(labelElement);
    return elementWidth;
}
function useTabStructure(options) {
    const { activeTab, children, lostProps, onNavigateTo, onTabClick, tabContentRef, showingTabsContainerRef, style, } = options;
    const [tabs, setTabs] = useState([]);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);
    useLayoutEffect(() => {
        const inheritedProperties = {
            ...lostProps,
            activeTab,
            isSideTab: false,
            onClick: onTabClick,
            onNavigateTo,
            style,
            tabContentRef: tabContentRef.current,
        };
        const tabSizes = new Map();
        const shouldHideExcessTabs = Boolean(showingTabsContainerRef.current) && !isTouchableDevice();
        const mapTabs = (tab) => {
            const tabElement = cloneElement(tab, {
                ...tab.props,
                ...inheritedProperties,
                enabled: tab.props.enabled ?? true,
                visible: tab.props.visible ?? true,
            });
            if (shouldHideExcessTabs && tabElement.props.name) {
                const width = getPreTabElementWidth(tabElement);
                tabSizes.set(tabElement.props.name, width);
            }
            return tabElement;
        };
        let mappedTabs = Children.map(children, mapTabs);
        if (shouldHideExcessTabs) {
            const maxShowingTabsWidth = showingTabsContainerRef.current?.getBoundingClientRect().width ?? 0;
            let currentShowingTabsWidth = tabSizes.get(activeTab ?? '') ?? 0;
            mappedTabs = mappedTabs.map(tab => {
                const isActiveTab = isDefined(tab.props.name) && tab.props.name === activeTab;
                if (!isActiveTab) {
                    currentShowingTabsWidth += tabSizes.get(tab.props.name) ?? 0;
                }
                return cloneElement(tab, {
                    isSideTab: !isActiveTab && currentShowingTabsWidth > maxShowingTabsWidth,
                });
            });
        }
        setTabs(mappedTabs);
    }, [activeTab, children, showingTabsContainerRef, style, windowWidth]);
    const tabStructure = useMemo(() => {
        return tabs.reduce((acc, tab) => {
            if (tab.props.isSideTab) {
                acc.side.push(tab.props);
            }
            else {
                acc.header.push(tab);
            }
            return acc;
        }, { header: [], side: [] });
    }, [tabs]);
    return tabStructure;
}
export { useTabStructure, };
//# sourceMappingURL=use-tab-structure.js.map